/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import {
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { from, Observable, Subject, take, takeUntil } from 'rxjs';
import { SysquePopupAdDirective } from './sysque-popup-adDirective';
import { ToolGroup } from '../models/tool-item-group';
import { SysquePopupConfig } from '../models/sysque-popup-config';
import {
  APP_SHELL_FACADE_TOKEN,
  IAppShellFacade,
} from '../../../../../toolbelt/data-access/src/lib/domain/facades-models/app-shell-facade-interface';
import { GlobalConstants } from '../constants/global-constants';
import { DownloadFamiliesReturnCodes } from '../models/download-families-return-codes';
import { ElectricalSettingsReturnCodes } from '../models/electrical-settings-return-codes';
import { createDialog } from '../popup-dialog-factory/popup-dialog.facade';
import { ShiftSegmentReturnCodes } from '../../../../../shared/ui/src/lib/enums/shift-segment-return-codes';
import { RotateElementReturnCodes } from '../../../../../shared/ui/src/lib/enums/rotate-element-return-codes';
import { SystemPanelFrameComponent } from '../components/system-panel-frame/system-panel-frame.component';
import { PastedItemsInfo } from '../models/pasted-items-info';
import { Item } from '../../../../data-access/src/lib/domain/item';
import { SysQueCopyPasteService } from '../../../../utils/src/lib/sysque-copy-paste/sysque-copy-paste.service';
import { WorksharingService } from '../../../../../toolbelt/utils/src/lib/sysque-worksharing/sysque-worksharing.service';
import { ProcessingService } from '../../../../../toolbelt/utils/src/lib/sysque-processing/sysque-processing.service';
import { DockableMessagesService } from '../../../../../toolbelt/utils/src/lib/sysque-dockable-messages/sysque-dockable-messages.service';
import { SysQueGaugeReturnCodes } from '../models/sysque-gauge-return-codes';
import { AcrylicSurfaceService } from '../acrylic-surface/acrylic-surface.service';
import { PanelCircuitDataReturnCodes } from '../models/panel-circuit-data-return-codes';
import { ConvertOptionReturnCodes } from '../models/place-coupling-return-codes';
import { AlertStatus } from '../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'sysque-app-shell',
  templateUrl: './app-shell.component.html',
  styleUrls: ['./app-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AppShellComponent implements OnInit, OnDestroy {
  @ViewChild(SysquePopupAdDirective, { static: true }) adPopupDirective!: SysquePopupAdDirective;
  public elementMovementDialog = '';
  public showElementMovement = false;
  public processEnabled = true;
  public alertName = '';
  public toolBeltItems$: Observable<ToolGroup[]>;
  public config: SysquePopupConfig = null;
  public isMsgPopupVisible: boolean = false;
  public selectSysquePopup$: Observable<SysquePopupConfig>;
  public isLoading = false;
  public areMissing = false;
  public isError = false;
  public dialogTitle = '';
  public dialogBody = '';
  public isButtonNoDisabled = true;
  public alertType: AlertStatus;
  public alertMessage: string;
  public isAlertShown = false;
  public isInvalidShortcut = false;
  public confirmationButton = '';
  public disapprovalButton = '';
  public isDisapprovalButtonDisabled = true;
  public isTetherConfiguration = false;
  public downloadType: string;

  //#region Copy/Paste members

  public replaceAll = false;
  public showDialog = false;
  public duplicateItemList: PastedItemsInfo[];
  public duplicateListIndex = 0;
  public itemName = '';
  public currentItem: Item;
  public destSelectedSystem: string;
  public isEditDuplicateElectricalItemAlert = false;
  @ViewChild(SystemPanelFrameComponent) systemPanelFrameComponent: SystemPanelFrameComponent;

  //#endregion

  //#region Worksharing members

  public isWorksharingSyncNecessary = false;
  public isDuplicateTypeNotFound = false;
  public isWorksharingSyncInProgress = false;
  private readonly closed$ = new Subject<void>();

  //#endregion

  //#region Processing members

  public isProgressBarVisible = false;
  public isProcessingNoAccess = false;
  public isProcessingNoActiveDocument = false;
  public isProcessingExtendTrimError = false;
  public isProcessingConduitRunsBendAngleError = false;
  public isProcessingConduitRunsAngleError = false;
  public isMaximumAngleExceeded = false;
  public isNecMaximumFill = false;
  public maxAngle = 0;
  public bendAngle = 0;

  //#endregion

  //#region Dockable Error Message Popup

  public isSelectionMismatchCableTray = false;
  public isInvalidSelection = false;
  public isSelectionMismatchConduits = false;
  public isFamilyNotExists = false;
  public isSelectionMismatchEquipment = false;
  public isSpecificationMissing = false;
  public isCodeCheckingExceedingMaximumAngle = false;
  public isWireFillSpecInformationNotAvailable = false;
  public isCommentsOrConduitIdMissing = false;
  public isConduitFillExceeds40PercentCheckingOrNotError = false;
  public isUnableToProcessFittingsError = false;
  //#endregion

  //#region popup dialog and progress bar members

  public progressBarTitle = '';
  public progressBarBody = '';
  public popupDialogTitle = '';
  public popupDialogBody = '';

  //#endregion

  constructor(
    @Inject(APP_SHELL_FACADE_TOKEN) public facade: IAppShellFacade,
    public copyPasteService: SysQueCopyPasteService,
    private worksharingService: WorksharingService,
    private processingService: ProcessingService,
    private dockableMessagesService: DockableMessagesService,
    private acrylicSurfaceService: AcrylicSurfaceService,
    private cd: ChangeDetectorRef
  ) {}

  public ngOnDestroy(): void {
    window.removeEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
    this.closed$.next();
    this.closed$.complete();
  }

  public async ngOnInit(): Promise<void> {
    this.acrylicSurfaceService.hide();
    window.addEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
    this.facade.initializeData();
    this.toolBeltItems$ = this.facade.selectToolBeltItems();
    this.selectSysquePopup$ = this.facade.selectSysquePopup();

    this.selectSysquePopup$.subscribe((comp: SysquePopupConfig) => this.resolvePopupVisibility(comp));
    this.facade.updateStorage(GlobalConstants.MSG_TITLE, '');
    this.facade.updateStorage(GlobalConstants.MSG_CONTENT, '');

    this.initializeSubscriptions();
  }

  public shouldDisplayPopup(): boolean {
    return (
      this.isProcessingNoAccess ||
      this.isProcessingNoActiveDocument ||
      this.isInvalidSelection ||
      this.isWorksharingSyncNecessary ||
      this.isDuplicateTypeNotFound ||
      this.isProcessingExtendTrimError ||
      this.isProcessingConduitRunsBendAngleError ||
      this.isProcessingConduitRunsAngleError ||
      this.isSelectionMismatchCableTray ||
      this.isSelectionMismatchConduits ||
      this.isSelectionMismatchEquipment ||
      this.isFamilyNotExists ||
      this.isSpecificationMissing ||
      this.isCodeCheckingExceedingMaximumAngle ||
      this.isWireFillSpecInformationNotAvailable ||
      this.isCommentsOrConduitIdMissing ||
      this.isConduitFillExceeds40PercentCheckingOrNotError ||
      this.isUnableToProcessFittingsError
    );
  }

  public getPopupMessageType(): string {
    if (this.isProcessingNoAccess) {
      return GlobalConstants.PROCESSING_NO_ACCESS;
    }
    if (this.isProcessingNoActiveDocument) {
      return GlobalConstants.PROCESSING_NO_ACTIVE_DOCUMENT;
    }
    if (this.isWorksharingSyncNecessary) {
      return GlobalConstants.WORKSHARING_SYNC;
    }
    if (this.isDuplicateTypeNotFound) {
      return GlobalConstants.NO_DUPLICATE_TYPE;
    }
    if (this.isProcessingExtendTrimError) {
      return GlobalConstants.PROCESSING_EXTEND_TRIM_ERROR;
    }
    if (this.isProcessingConduitRunsBendAngleError) {
      return GlobalConstants.PROCESSING_CONDUIT_RUNS_BEND_ANGLE_ERROR;
    }
    if (this.isProcessingConduitRunsAngleError) {
      return GlobalConstants.PROCESSING_CONDUIT_RUNS_ANGLE_ERROR;
    }
    if (this.isSelectionMismatchCableTray) {
      return GlobalConstants.SELECTION_MISMATCH_CABLETRAY;
    }
    if (this.isInvalidSelection) {
      return GlobalConstants.INVALID_SELECTION;
    }
    if (this.isSelectionMismatchConduits) {
      return GlobalConstants.SELECTION_MISMATCH_CONDUITS;
    }
    if (this.isSelectionMismatchEquipment) {
      return GlobalConstants.SELECTION_MISMATCH_EQUIPMENT;
    }
    if (this.isFamilyNotExists) {
      return GlobalConstants.FAMILY_NOT_EXISTS;
    }
    if (this.isSpecificationMissing) {
      return GlobalConstants.SPECIFICATION_MISSING;
    }
    if (this.isCodeCheckingExceedingMaximumAngle) {
      return GlobalConstants.CODE_CHECKING_EXCEEDING_MAXIMUM_ANGLE;
    }
    if (this.isWireFillSpecInformationNotAvailable) {
      return GlobalConstants.WIRE_FILL_SPEC_INFORMATION_NOT_AVAILABLE;
    }
    if (this.isCommentsOrConduitIdMissing) {
      return GlobalConstants.COMMENTS_OR_CONDUIT_ID_MISSING;
    }
    if (this.isConduitFillExceeds40PercentCheckingOrNotError) {
      return GlobalConstants.CONDUITFILL_EXCEEDING_40PERCENT_CHECKING;
    }
    if (this.isUnableToProcessFittingsError) {
      return GlobalConstants.UNABLE_TO_PROCESS_FITTINGS_ERROR;
    }
  }

  public checkInvalidShortcuts(): void {
    const result: Observable<ElectricalSettingsReturnCodes> = this.facade.checkInvalidShortcuts();
    result.subscribe((response) => {
      if (response === ElectricalSettingsReturnCodes.InvalidShortcutsOpening) {
        this.isInvalidShortcut = true;
        this.dialogTitle = GlobalConstants.InvalidShortcutsTitle;
        this.dialogBody = GlobalConstants.InvalidShortcutsBody;
      }
    });
  }

  public openElectricalSettings(): void {
    let sysquePopupConfig = new SysquePopupConfig();
    createDialog(sysquePopupConfig, GlobalConstants.ELECTRICAL_SETTINGS_DIALOG);
    this.facade.sendPopupRoutingAction(
      GlobalConstants.ELECTRICAL_SETTINGS_DIALOG,
      sysquePopupConfig.width,
      sysquePopupConfig.height
    );
    this.isInvalidShortcut = false;
  }

  public onShiftValueOk(): void {
    this.elementMovementDialog = '';
    this.showElementMovement = false;
  }

  /**
   * Handle the emitted process enabled toggle value
   * @param processEnabled The value of the process enable toggle
   */
  public emitProcessEnable(processEnabled: boolean): void {
    this.processEnabled = processEnabled;
    this.alertType = AlertStatus.Warning;
    this.alertName = GlobalConstants.ProcessEnabledTitle;
    this.alertMessage = GlobalConstants.ProcessEnabledStop;
  }

  /**
   * Handle the emitted return code
   * @param returnCode The emitted return code after the event of the rotation of an element
   */
  public emitRotateElementReturnCode(returnCode: RotateElementReturnCodes): void {
    switch (returnCode) {
      case RotateElementReturnCodes.AssemblySelected:
        this.elementMovementDialog = GlobalConstants.AssemblySelected;
        break;
      case RotateElementReturnCodes.IncorrectStructure:
        this.elementMovementDialog = GlobalConstants.RotateElementIncorrectStructure;
        break;
      case RotateElementReturnCodes.InvalidSelection:
        this.elementMovementDialog = GlobalConstants.RotateElementInvalidSelection;
        break;
    }
    this.showElementMovement = true;
  }

  /**
   * Handle the emitted return code
   * @param returnCode The emitted return code after the event of the shifting of an element
   */
  public emitShiftElementReturnCode(returnCode: ShiftSegmentReturnCodes): void {
    if (returnCode === ShiftSegmentReturnCodes.AssemblySelected) {
      this.elementMovementDialog = GlobalConstants.AssemblySelected;
      this.showElementMovement = true;
    } else if (returnCode === ShiftSegmentReturnCodes.FieldFabSelected) {
      this.elementMovementDialog = GlobalConstants.ShiftElementFieldFabSelected;
      this.showElementMovement = true;
    }
  }

  public onConfirmationClick(): void {
    this.areMissing = false;
    if (this.confirmationButton === GlobalConstants.Yes) {
      switch (this.downloadType) {
        case GlobalConstants.MissingFamiliesType:
          this.missingFamiliesLogic();
          break;
        case GlobalConstants.GenericFamilies:
          this.updateGenericFittingsLogic();
          break;
      }
    } else if (this.confirmationButton === GlobalConstants.Dismiss) {
      this.isError = false;
      this.checkInvalidShortcuts();
    }
  }

  public onDisapprovalClick(): void {
    this.areMissing = false;
    this.isLoading = false;
    this.checkInvalidShortcuts();
  }

  public onOkClick(): void {
    this.isEditDuplicateElectricalItemAlert = false;
    this.facade.updateLocalStorage(GlobalConstants.SHOW_EDIT_DUPLICATE_ITEM_WINDOW, true);
  }

  public onNoClick(): void {
    this.isEditDuplicateElectricalItemAlert = false;
    this.facade.updateLocalStorage(GlobalConstants.SHOW_EDIT_DUPLICATE_ITEM_WINDOW, false);
  }

  public resolvePopupVisibility(config: SysquePopupConfig): void {
    if (config) {
      this.config = config;

      const container = this.adPopupDirective.viewContainerRef;
      container.clear();
      const compRef = container.createComponent(config.component);
      if (compRef) {
        compRef.instance['data'] = config.data;
        compRef.instance['dragEnabled'] = config.dragEnabled;
        compRef.instance['height'] = config.height;
        compRef.instance['width'] = config.width;
        compRef.instance['hideOnOutsideClick'] = config.hideOnOutsideClick;
        compRef.instance['showCloseButton'] = config.showCloseButton;
        compRef.instance['componentTitle'] = config.componentTitle;
        compRef.instance['visible'] = config.visible;
        compRef.instance['close'].subscribe(() => {
          this.facade.dispatchShowSysquePopupAction({ sysquePopupConfig: null });
        });
      }
    }
  }

  public onDoneClick(): void {
    this.facade.onDoneAction();
  }

  public okClick(): void {
    if (this.isAlertShown) {
      this.alertMessage = '';
      this.isAlertShown = false;
    }

    this.isMsgPopupVisible = false;
    this.facade.updateStorage(GlobalConstants.MSG_TITLE, '');
    this.facade.updateStorage(GlobalConstants.MSG_CONTENT, '');
    this.dialogTitle = '';
    this.dialogBody = '';
    this.acrylicSurfaceService.hide();
  }

  public onCloseButtonAlertClicked(): void {
    this.isAlertShown = false;
    this.alertMessage = '';
  }

  public okClickPopupMessage(messageType: string): void {
    switch (messageType) {
      case GlobalConstants.NO_DUPLICATE_TYPE:
        this.worksharingService.setDefaultTypeNotFoundForDuplicate(false);
        break;
      case GlobalConstants.WORKSHARING_SYNC:
        this.worksharingService.setWorksharingSyncNecessary(false);
        break;
      case GlobalConstants.PROCESSING_NO_ACCESS:
        this.processingService.setProcessingWorksharingNoAccess(false);
        break;
      case GlobalConstants.PROCESSING_NO_ACTIVE_DOCUMENT:
        this.processingService.setProcessingNoActiveDocument(false);
        break;
      case GlobalConstants.PROCESSING_EXTEND_TRIM_ERROR:
        this.processingService.setProcessingExtendTrimError(false);
        break;
      case GlobalConstants.PROCESSING_CONDUIT_RUNS_BEND_ANGLE_ERROR:
        this.processingService.setProcessingConduitRunsBendAngleError(false);
        break;
      case GlobalConstants.PROCESSING_CONDUIT_RUNS_ANGLE_ERROR:
        this.processingService.setProcessingConduitRunsAngleError(0, 0);
        break;
      case GlobalConstants.SELECTION_MISMATCH_CABLETRAY:
        this.dockableMessagesService.setSelectionMismatchCodeChecking(false);
        break;
      case GlobalConstants.INVALID_SELECTION:
        this.dockableMessagesService.setInvalidSelection(false);
        break;
      case GlobalConstants.SELECTION_MISMATCH_CONDUITS:
        this.dockableMessagesService.setSelectionMismatchConduits(false);
        break;
      case GlobalConstants.FAMILY_NOT_EXISTS:
        this.dockableMessagesService.setFamilyNotExists(false);
        break;
      case GlobalConstants.SELECTION_MISMATCH_EQUIPMENT:
        this.dockableMessagesService.setSelectionMismatchEquipment(false);
        break;
      case GlobalConstants.SPECIFICATION_MISSING:
        this.dockableMessagesService.setSpecificationMissing(false);
        break;
      case GlobalConstants.CODE_CHECKING_EXCEEDING_MAXIMUM_ANGLE:
        this.dockableMessagesService.setCodeCheckingExceedingMaximumAngle(false, 0, 0);
        break;
      case GlobalConstants.WIRE_FILL_SPEC_INFORMATION_NOT_AVAILABLE:
        this.dockableMessagesService.setWireFillSpecInformationNotAvailable(false);
        break;
      case GlobalConstants.COMMENTS_OR_CONDUIT_ID_MISSING:
        this.dockableMessagesService.setCommentsOrConduitIdMissing(false);
        break;
      case GlobalConstants.CONDUITFILL_EXCEEDING_40PERCENT_CHECKING:
        this.dockableMessagesService.setConduitFillExceeds40PercentCheckingOrNotError(false, false);
        break;
      case GlobalConstants.UNABLE_TO_PROCESS_FITTINGS_ERROR:
        this.dockableMessagesService.setUnableToProcessFittingsError(false);
        break;
    }
  }

  public handleStorageEvent(event: StorageEvent): void {
    this.isMsgPopupVisible = false;
    if (event.newValue === '') {
      return;
    }
    const response = JSON.parse(event.newValue);

    switch (event.key) {
      case GlobalConstants.SHOW_LINKED_PROJECT_MESSAGE: {
        this.showLinkedProjectMessage();
        break;
      }
      case GlobalConstants.MSG_TITLE:
      case GlobalConstants.MSG_CONTENT: {
        this.messageTitleContent();
        break;
      }
      case GlobalConstants.DuplicateItemsUpdate: {
        if (response) {
          this.onShowDuplicateError(this.duplicateItemList);
        }
        break;
      }
      case GlobalConstants.SYSQUE_CONNECT_RESULT: {
        this.sysqueConnectResult(response);
        break;
      }
      case GlobalConstants.UPDATE_FAMILIES: {
        this.genericFittingsDialog();
        break;
      }
      case GlobalConstants.DOWNLOAD_MISSING_FAMILIES: {
        this.handleDownloadMissingFamilies();
        break;
      }
      case GlobalConstants.DOWNLOAD_GENERIC_FAMILIES: {
        this.handleDownloadgenericFamilies();
        break;
      }
      case GlobalConstants.CHECK_MISSING_FAMILIES: {
        this.handleCheckMissingFamilies(response);
        break;
      }
      case GlobalConstants.PANEL_DATA_VALIDATION_RESPONSE: {
        this.handlePanelDataValidationResponse(response);
        break;
      }
      case GlobalConstants.CONVERT_SYSTEM_RESPONSE: {
        this.handleConvertSystemResponse(response);
        break;
      }
      case GlobalConstants.SHOW_EDIT_DUPLICATE_ITEM_ALERT: {
        this.hanldeEditDuplicateItemAlert();
        break;
      }
      case GlobalConstants.NO_SYSTEM_SELECTED: {
        if (!response) {
          return;
        }
        this.setDialogProperties(GlobalConstants.TitleNoSystemSelected, GlobalConstants.BodyNoSystemSelected);
        break;
      }
      case GlobalConstants.SYSTEM_NAME_DOES_NOT_EXIST: {
        if (!response) {
          return;
        }
        this.setDialogProperties(GlobalConstants.InvalidSelection, GlobalConstants.SystemNameDoesNotExist);
        break;
      }
      case GlobalConstants.FEATURE_MAY_NOT_WORK_WITH_SELECTION: {
        if (!response) {
          return;
        }
        this.setDialogProperties(GlobalConstants.InvalidSelection, GlobalConstants.FeatureMayNotWorkWithSelection);
        break;
      }
      case GlobalConstants.SELECT_CONDUIT_CABLE_TRAY_FITTING: {
        if (!JSON.parse(event.newValue)) {
          return;
        }
        this.setDialogProperties(GlobalConstants.InvalidSelection, GlobalConstants.SelectConduitCableTrayFitting);
        break;
      }
    }
  }

  //#region Handle storage events

  private showLinkedProjectMessage(): void {
    this.isTetherConfiguration = true;
    this.popupDialogTitle = GlobalConstants.TetherConfigurationTitle;
    this.popupDialogBody = GlobalConstants.TetherConfigurationText;
    this.cd.detectChanges();
  }

  private messageTitleContent(): void {
    this.dialogTitle = this.facade.getStorage(GlobalConstants.MSG_TITLE) as string;
    this.dialogBody = this.facade.getStorage(GlobalConstants.MSG_CONTENT) as string;
    if (this.dialogTitle && this.dialogBody) {
      this.isMsgPopupVisible = true;
      this.cd.detectChanges();
    }
  }

  private sysqueConnectResult(response: SysQueGaugeReturnCodes): void {
    if (response === SysQueGaugeReturnCodes.ResetAll) {
      this.facade.updateLocalStorage(GlobalConstants.IS_SYSQUE_CONNECT_ON, false);
      this.facade.updateLocalStorage(GlobalConstants.RELOAD_FITTINGS, true);
      this.cd.detectChanges();
    }
    if (response === SysQueGaugeReturnCodes.Reset) {
      this.facade.updateLocalStorage(GlobalConstants.IS_SYSQUE_CONNECT_ON, false);
    }
    if (response === SysQueGaugeReturnCodes.FaceBased) {
      this.getFaceBasedAlertInfo();
    }

    if (response === SysQueGaugeReturnCodes.ShowAcrylicSurface) {
      this.facade.updateLocalStorage(GlobalConstants.SHOW_ACRYLIC_SURFACE, true);
      return;
    }
    this.facade.updateLocalStorage(GlobalConstants.SHOW_ACRYLIC_SURFACE, false);
  }

  private getFaceBasedAlertInfo(): void {
    this.dialogTitle = GlobalConstants.TitleForSysQueConnect;
    this.dialogBody = GlobalConstants.BodyForSysQueConnect;
    this.isMsgPopupVisible = true;
  }

  private setDialogProperties(title: string, body: string): void {
    this.isError = true;
    this.dialogTitle = title;
    this.dialogBody = body;
    this.confirmationButton = GlobalConstants.Dismiss;
    this.isDisapprovalButtonDisabled = true;
    this.cd.detectChanges();
  }

  private handleDownloadMissingFamilies(): void {
    this.acrylicSurfaceService.hide();
    from(this.facade.checkIfMissingFamilies())
      .pipe(take(1))
      .subscribe((result) => {
        this.checkIfMissingFamilies(result, true);
      });
  }

  private handleDownloadgenericFamilies(): void {
    this.isLoading = true;
    this.progressBarTitle = GlobalConstants.Downloading;
    this.progressBarBody = GlobalConstants.GenericFamilies;
    this.cd.detectChanges();
  }

  private handleCheckMissingFamilies(response: boolean): void {
    this.isLoading = false;
    this.cd.detectChanges();
    let codeResponse = response ? DownloadFamiliesReturnCodes.Download : DownloadFamiliesReturnCodes.NotDownload;
    this.checkIfMissingFamilies(codeResponse, false);
  }

  private handlePanelDataValidationResponse(response: PanelCircuitDataReturnCodes): void {
    this.isMsgPopupVisible = true;
    this.getApplyPanelAlertInfo(response);
    this.cd.detectChanges();
  }

  private handleConvertSystemResponse(response: ConvertOptionReturnCodes): void {
    this.isMsgPopupVisible = true;
    this.getConvertSystemAlertInfo(response);
    this.cd.detectChanges();
  }

  private hanldeEditDuplicateItemAlert(): void {
    this.isEditDuplicateElectricalItemAlert = true;
    this.dialogTitle = GlobalConstants.TitleForEditDuplicateElectricalItem;
    this.dialogBody = GlobalConstants.BodyForEditDuplicateElectricalItem;
    this.cd.detectChanges();
  }

  //#endregion

  //#region Copy Paste logic

  public onPasteError(event: string): void {
    this.alertMessage = event;
    this.alertType = AlertStatus.Warning;
    this.showDialog = false;
    this.isAlertShown = true;
  }

  public onShowDuplicateError(event: PastedItemsInfo[]): void {
    if (this.duplicateItemList && this.duplicateListIndex > this.duplicateItemList.length - 1) {
      this.executePasteLogic();
      return;
    }
    if (!this.duplicateItemList) {
      this.copyPasteService.setDuplicateItemsList([]);
      this.facade.updateLocalStorage(GlobalConstants.CanExecutePasteLogic, false);
      this.duplicateItemList = event;
      this.replaceAll = false;
    }

    if (!this.replaceAll) {
      this.getAlertInfo();
    }
  }

  public executePasteLogic(): void {
    this.duplicateItemList = this.duplicateItemList.filter((item) => !item.isCancel);
    if (this.duplicateItemList.length > 0) {
      this.copyPasteService.setDuplicateItemsList(this.duplicateItemList);
      this.facade.updateLocalStorage(GlobalConstants.CanExecutePasteLogic, true);
    }
    this.duplicateItemList = null;
    this.duplicateListIndex = 0;
    this.replaceAll = false;
    this.cd.detectChanges();
  }

  public getAlertInfo(): void {
    this.itemName = this.duplicateItemList[this.duplicateListIndex].copiedItem.name;
    this.currentItem = this.duplicateItemList[this.duplicateListIndex].copiedItem;
    this.destSelectedSystem = this.duplicateItemList[this.duplicateListIndex].destSystemName;
    this.dialogTitle = GlobalConstants.ReplaceItemTitle;
    this.dialogBody = GlobalConstants.ReplaceItemContent + this.itemName + ' in ' + this.destSelectedSystem + '?';
    this.showDialog = true;
    this.duplicateListIndex++;
  }

  public onReplaceClick(): void {
    if (this.replaceAll) {
      this.updateItemsOnReplaceAllCheck();
    } else {
      this.updateItemsOnReplaceClick();
    }
  }

  public updateItemsOnReplaceClick(): void {
    this.showDialog = false;
    let indexToUpdate = this.duplicateItemList.findIndex((item) => item.copiedItem.name === this.itemName);
    this.duplicateItemList[indexToUpdate].isReplace = true;
    this.copyPasteService.setDuplicateItemsList(this.duplicateItemList);
    this.onShowDuplicateError(this.duplicateItemList);
  }

  public onCancelClick(): void {
    this.showDialog = false;
    let indexToUpdate = this.duplicateItemList.findIndex((item) => item.copiedItem.name === this.itemName);
    this.duplicateItemList[indexToUpdate].isCancel = true;
    if (this.replaceAll) {
      this.replaceAll = false;
    }
    this.systemPanelFrameComponent.removeItem(this.currentItem);
  }

  public onReplaceAll(event: any): void {
    this.replaceAll = event.detail;
  }

  public updateItemsOnReplaceAllCheck(): void {
    this.showDialog = false;
    this.duplicateItemList = this.duplicateItemList.map((item) => {
      if (item.isDuplicate) {
        return { ...item, isReplaceAll: true };
      } else {
        return item;
      }
    });
    this.executePasteLogic();
  }

  //#endregion

  public onOkClickTetherConfiguration(): void {
    if (!this.isTetherConfiguration) {
      this.okClickPopupMessage(this.getPopupMessageType());
    }
    this.isTetherConfiguration = false;
    localStorage.removeItem(GlobalConstants.SHOW_LINKED_PROJECT_MESSAGE);
    this.acrylicSurfaceService.hide();
    this.cd.detectChanges();
  }

  private initializeSubscriptions(): void {
    this.worksharingService.worksharingSyncNecessary$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'SyncNecessary'));

    this.worksharingService.defaultTypeNotFoundForDuplicate$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'NoDuplicateType'));

    this.processingService.processingWorksharingNoAccess$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'NoAccess'));

    this.processingService.processingNoActiveDocument$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'NoActiveDocument'));

    this.processingService.processingExtendTrimError$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'ExtendTrimError'));

    this.processingService.processingConduitRunsBendAngleError$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'ConduitRunsBendAngleError'));

    this.processingService.processingConduitRunsAngleError$
      .pipe(takeUntil(this.closed$))
      .subscribe(([maxAngle, bendAngle]) => {
        this.maxAngle = maxAngle;
        this.bendAngle = bendAngle;
        if (maxAngle !== 0 && bendAngle !== 0) {
          this.setPopupDialogTitleAndBody(true, 'ConduitRunsAngleError');
        } else {
          this.setPopupDialogTitleAndBody(false, 'ConduitRunsAngleError');
        }
      });

    this.dockableMessagesService.codeCheckingExceedingMaximumAngle$
      .pipe(takeUntil(this.closed$))
      .subscribe(([isExceeded, [maxAngle, bendAngle]]) => {
        this.isMaximumAngleExceeded = isExceeded;
        this.maxAngle = maxAngle;
        this.bendAngle = bendAngle;
        if (maxAngle !== 0 && bendAngle !== 0) {
          this.setPopupDialogTitleAndBody(true, 'CodeCheckingExceedingMaximumAngle');
        } else {
          this.setPopupDialogTitleAndBody(false, 'CodeCheckingExceedingMaximumAngle');
        }
        this.cd.markForCheck();
      });

    this.dockableMessagesService.selectionMismatchCodeChecking$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'SelectionMismatchCableTray'));

    this.dockableMessagesService.invalidSelection$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'InvalidSelection'));

    this.dockableMessagesService.selectionMismatchConduits$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'SelectionMismatchConduits'));

    this.dockableMessagesService.specificationMissing$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'SpecificationMissing'));

    this.dockableMessagesService.selectionMismatchEquipment$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'SelectionMismatchEquipment'));

    this.dockableMessagesService.familyNotExists$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'FamilyNotExists'));

    this.dockableMessagesService.wireFillSpecInformationNotAvailable$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'WireFillSpecInformationNotAvailable'));

    this.dockableMessagesService.commentsOrConduitIdMissing$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'CommentsOrConduitIdMissing'));

    this.dockableMessagesService.conduitFillExceeds40PercentCheckingOrNotError$
      .pipe(takeUntil(this.closed$))
      .subscribe(([isExceeded, isNecMaximumFill]) => {
        this.isMaximumAngleExceeded = isExceeded;
        this.isNecMaximumFill = isNecMaximumFill;

        if (this.isMaximumAngleExceeded || this.isNecMaximumFill) {
          this.setPopupDialogTitleAndBody(true, 'ConduitFillExceeds40PercentCheckingOrNotError');
        } else {
          this.setPopupDialogTitleAndBody(false, 'ConduitFillExceeds40PercentCheckingOrNotError');
        }
      });

    this.dockableMessagesService.unableToProcessFittingsError$
      .pipe(takeUntil(this.closed$))
      .subscribe((value) => this.setPopupDialogTitleAndBody(value, 'UnableToProcessFittingsError'));

    this.worksharingService.worksharingInProgress$.pipe(takeUntil(this.closed$)).subscribe((value) => {
      this.isWorksharingSyncInProgress = value;
      if (value) {
        this.progressBarBody = GlobalConstants.WorksharingProgressBody;
        this.progressBarTitle = GlobalConstants.WorksharingProgressTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
      }
      this.cd.detectChanges();
    });

    this.processingService.initializeProcessing$.pipe(takeUntil(this.closed$)).subscribe((value) => {
      this.isProgressBarVisible = value;
      if (value) {
        this.progressBarBody = GlobalConstants.ProcessingStartBody;
        this.progressBarTitle = GlobalConstants.ProcessingTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
        this.acrylicSurfaceService.hide();
      }
      this.cd.detectChanges();
    });

    this.processingService.processingCableTrayFittings$.pipe(takeUntil(this.closed$)).subscribe((value) => {
      this.isProgressBarVisible = value !== 0;
      if (value !== 0) {
        this.progressBarBody =
          GlobalConstants.ProcessingTitle + ' ' + value + ' ' + GlobalConstants.ProcessingCableTrayFittings;
        this.progressBarTitle = GlobalConstants.ProcessingTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
      }
      this.cd.detectChanges();
    });

    this.dockableMessagesService.processingApplySystemData$.pipe(takeUntil(this.closed$)).subscribe((value) => {
      this.isProgressBarVisible = value !== 0;
      if (value !== 0) {
        this.progressBarBody = value + ' ' + GlobalConstants.ProcessingElements;
        this.progressBarTitle = GlobalConstants.ProcessingTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
      }
      this.cd.detectChanges();
    });

    this.dockableMessagesService.updatingNominalSizeStatus$.pipe(takeUntil(this.closed$)).subscribe((totalCount) => {
      this.isProgressBarVisible = totalCount !== 0;
      if (totalCount !== 0) {
        this.progressBarBody =
          GlobalConstants.UpdatingNominalSizeStatusBody +
          ' ' +
          totalCount +
          ' ' +
          GlobalConstants.UpdatingNominalSizeStatusBodyElements;
        this.progressBarTitle = GlobalConstants.ApplyNominalSizeTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
      }
      this.cd.detectChanges();
    });

    this.dockableMessagesService.updatingExtendedDataStatus$.pipe(takeUntil(this.closed$)).subscribe((totalCount) => {
      this.isProgressBarVisible = totalCount !== 0;
      if (totalCount !== 0) {
        this.progressBarBody =
          GlobalConstants.UpdatingExtendedDataStatusBody +
          ' ' +
          totalCount +
          ' ' +
          GlobalConstants.UpdatingNominalSizeStatusBodyElements;
        this.progressBarTitle = GlobalConstants.UpdateExtendedDataStatusTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
      }
      this.cd.detectChanges();
    });

    this.processingService.processingCableTrays$.pipe(takeUntil(this.closed$)).subscribe((value) => {
      this.isProgressBarVisible = value !== 0;
      if (value !== 0) {
        this.progressBarBody =
          GlobalConstants.ProcessingTitle + ' ' + value + ' ' + GlobalConstants.ProcessingCableTrays;
        this.progressBarTitle = GlobalConstants.ProcessingTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
      }
      this.cd.detectChanges();
    });

    this.processingService.processingConduitFittings$.pipe(takeUntil(this.closed$)).subscribe((value) => {
      if (value !== 0) {
        this.progressBarBody =
          GlobalConstants.ProcessingTitle + ' ' + value + ' ' + GlobalConstants.ProcessingConduitFittings;
        this.progressBarTitle = GlobalConstants.ProcessingTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
      }
      this.cd.detectChanges();
    });

    this.processingService.processingConduits$.pipe(takeUntil(this.closed$)).subscribe((value) => {
      if (value !== 0) {
        this.progressBarBody = GlobalConstants.ProcessingTitle + ' ' + value + ' ' + GlobalConstants.ProcessingConduits;
        this.progressBarTitle = GlobalConstants.ProcessingTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
      }
      this.cd.detectChanges();
    });

    this.processingService.processingFieldFabConduits$.pipe(takeUntil(this.closed$)).subscribe((value) => {
      if (value !== 0) {
        this.progressBarBody =
          GlobalConstants.ProcessingTitle + ' ' + value + ' ' + GlobalConstants.ProcessingFieldFabConduits;
        this.progressBarTitle = GlobalConstants.ProcessingTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
      }
      this.cd.detectChanges();
    });

    this.processingService.processingFabConduitsConnectivity$.pipe(takeUntil(this.closed$)).subscribe((value) => {
      this.isProgressBarVisible = value !== 0;
      if (this.isProgressBarVisible) {
        this.progressBarBody = value + ' ' + GlobalConstants.ProcessingFabConduitsConnectivityBody;
        this.progressBarTitle = GlobalConstants.ProcessingTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
      }
      this.cd.detectChanges();
    });

    this.processingService.processingTakeOffFittings$.pipe(takeUntil(this.closed$)).subscribe((value) => {
      this.isProgressBarVisible = value !== 0;
      if (this.isProgressBarVisible) {
        this.progressBarBody = value + ' ' + GlobalConstants.ProcessingFittings;
        this.progressBarTitle = GlobalConstants.ProcessingTitle;
      } else {
        this.progressBarBody = '';
        this.progressBarTitle = '';
      }
      this.cd.detectChanges();
    });
  }

  private setPopupDialogTitleAndBody(
    value: boolean,
    type:
      | 'NoAccess'
      | 'NoActiveDocument'
      | 'NoDuplicateType'
      | 'SyncNecessary'
      | 'ExtendTrimError'
      | 'SelectionMismatchConduits'
      | 'SelectionMismatchCableTray'
      | 'FamilyNotExists'
      | 'InvalidSelection'
      | 'SelectionMismatchEquipment'
      | 'SpecificationMissing'
      | 'CodeCheckingExceedingMaximumAngle'
      | 'WireFillSpecInformationNotAvailable'
      | 'CommentsOrConduitIdMissing'
      | 'ConduitRunsBendAngleError'
      | 'ConduitRunsAngleError'
      | 'ConduitFillExceeds40PercentCheckingOrNotError'
      | 'UnableToProcessFittingsError'
  ): void {
    let titleKey = '';
    let bodyKey = '';

    if (value) {
      switch (type) {
        case 'NoAccess':
          titleKey = 'ProcessingWorksharingNoAccessTitle';
          bodyKey = 'ProcessingWorksharingNoAccessBody';
          this.isProcessingNoAccess = true;
          break;
        case 'NoActiveDocument':
          titleKey = 'ProcessingNoActiveDocumentTitle';
          bodyKey = 'ProcessingNoActiveDocumentBody';
          this.isProcessingNoActiveDocument = true;
          break;
        case 'NoDuplicateType':
          titleKey = 'DuplicationDefaultTypeNotFoundTitle';
          bodyKey = 'DuplicationDefaultTypeNotFoundBody';
          this.isDuplicateTypeNotFound = true;
          break;
        case 'SyncNecessary':
          titleKey = 'WorksharingSyncNecessaryTitle';
          bodyKey = 'WorksharingSyncNecessaryBody';
          this.isWorksharingSyncNecessary = true;
          break;
        case 'ExtendTrimError':
          titleKey = 'ProcessingExtendTrimErrorTitle';
          bodyKey = 'ProcessingExtendTrimErrorBody';
          this.isProcessingExtendTrimError = true;
          break;
        case 'ConduitRunsBendAngleError':
          titleKey = 'ProcessingConduitRunsBendAngleErrorTitle';
          bodyKey = 'ProcessingConduitRunsBendAngleErrorBody';
          this.isProcessingConduitRunsBendAngleError = true;
          break;
        case 'ConduitRunsAngleError':
          this.popupDialogTitle = GlobalConstants.ProcessingConduitRunsBendAngleErrorTitle;
          this.popupDialogBody =
            GlobalConstants.ProcessingConduitRunsAngleErrorBody +
            this.maxAngle +
            GlobalConstants.ProcessingConduitRunsAngleErrorBodyCurrentRun +
            this.bendAngle +
            '.';
          this.cd.detectChanges();
          this.isProcessingConduitRunsAngleError = true;
          return;
        case GlobalConstants.CODE_CHECKING_EXCEEDING_MAXIMUM_ANGLE:
          this.popupDialogTitle = GlobalConstants.ProcessingConduitRunsBendAngleErrorTitle;

          if (this.isMaximumAngleExceeded) {
            this.popupDialogBody =
              GlobalConstants.ProcessingConduitRunsAngleErrorBody +
              this.maxAngle +
              GlobalConstants.ProcessingConduitRunsAngleErrorBodyCurrentRun +
              this.bendAngle +
              '.';
          } else {
            this.popupDialogBody =
              GlobalConstants.ProcessingConduitRunsAngleNotExceededBody +
              this.maxAngle +
              GlobalConstants.ProcessingConduitRunsAngleErrorBodyCurrentRun +
              this.bendAngle +
              '.';
          }

          this.cd.detectChanges();
          this.isCodeCheckingExceedingMaximumAngle = true;
          return;
        case GlobalConstants.SELECTION_MISMATCH_CABLETRAY:
          titleKey = 'SelectionMismatchCableTrayTitle';
          bodyKey = 'SelectionMismatchCableTrayBody';
          this.isSelectionMismatchCableTray = true;
          break;
        case GlobalConstants.INVALID_SELECTION:
          titleKey = 'InvalidSelectionTitle';
          bodyKey = 'InvalidSelectionBody';
          this.isInvalidSelection = true;
          break;
        case GlobalConstants.SELECTION_MISMATCH_CONDUITS:
          titleKey = 'SelectionMismatchCableTrayTitle';
          bodyKey = 'SelectionMismatchConduitsBody';
          this.isSelectionMismatchConduits = true;
          break;
        case GlobalConstants.FAMILY_NOT_EXISTS:
          titleKey = 'FamilyNotExistsTitle';
          bodyKey = 'FamilyNotExistsBody';
          this.isFamilyNotExists = true;
          break;
        case GlobalConstants.SELECTION_MISMATCH_EQUIPMENT:
          titleKey = 'SelectionMismatchCableTrayTitle';
          bodyKey = 'SelectionMismatchEquipmentBody';
          this.isSelectionMismatchEquipment = true;
          break;
        case GlobalConstants.SPECIFICATION_MISSING:
          titleKey = 'SpecificationMissingTitle';
          bodyKey = 'SpecificationMissingBody';
          this.isSpecificationMissing = true;
          break;
        case GlobalConstants.WIRE_FILL_SPEC_INFORMATION_NOT_AVAILABLE:
          titleKey = 'WireFillSpecInformationNotAvailableTitle';
          bodyKey = 'WireFillSpecInformationNotAvailableBody';
          this.isWireFillSpecInformationNotAvailable = true;
          break;
        case GlobalConstants.COMMENTS_OR_CONDUIT_ID_MISSING:
          titleKey = 'CommentsOrConduitIdMissingTitle';
          bodyKey = 'CommentsOrConduitIdMissingBody';
          this.isCommentsOrConduitIdMissing = true;
          break;
        case GlobalConstants.UNABLE_TO_PROCESS_FITTINGS_ERROR:
          titleKey = 'UnableToProcessFittingsErrorTitle';
          bodyKey = 'UnableToProcessFittingsErrorBody';
          this.isUnableToProcessFittingsError = true;
          break;
        case GlobalConstants.CONDUITFILL_EXCEEDING_40PERCENT_CHECKING:
          this.popupDialogTitle = GlobalConstants.ConduitFillExceeds40PercentCheckingOrNotErrorTitle;

          if (this.isMaximumAngleExceeded && this.isNecMaximumFill) {
            this.popupDialogBody = GlobalConstants.ConduitFillExceedsNecMAximumFillBody;
          } else if (this.isMaximumAngleExceeded) {
            this.popupDialogBody = GlobalConstants.ConduitFillExceeds40PercentCheckingErrorBody;
          } else {
            this.popupDialogBody = GlobalConstants.ConduitFillNotExceeds40PercentCheckingErrorBody;
          }

          this.cd.markForCheck();
          this.isConduitFillExceeds40PercentCheckingOrNotError = true;
          return;
      }
    } else {
      this.isProcessingNoAccess = false;
      this.isProcessingNoActiveDocument = false;
      this.isDuplicateTypeNotFound = false;
      this.isWorksharingSyncNecessary = false;
      this.isProcessingExtendTrimError = false;
      this.isProcessingConduitRunsBendAngleError = false;
      this.isProcessingConduitRunsAngleError = false;
      this.isSelectionMismatchCableTray = false;
      this.isSpecificationMissing = false;
      this.isSelectionMismatchEquipment = false;
      this.isFamilyNotExists = false;
      this.isInvalidSelection = false;
      this.isSelectionMismatchConduits = false;
      this.isCodeCheckingExceedingMaximumAngle = false;
      this.isWireFillSpecInformationNotAvailable = false;
      this.isCommentsOrConduitIdMissing = false;
      this.isConduitFillExceeds40PercentCheckingOrNotError = false;
      this.isUnableToProcessFittingsError = false;
    }

    this.popupDialogTitle = value ? GlobalConstants[titleKey] : '';
    this.popupDialogBody = value ? GlobalConstants[bodyKey] : '';
    this.cd.detectChanges();
  }

  private getApplyPanelAlertInfo(response: PanelCircuitDataReturnCodes): void {
    switch (response) {
      case PanelCircuitDataReturnCodes.NoSelection:
        this.dialogTitle = GlobalConstants.SelectApplyPanelSelectionRequired;
        this.dialogBody = GlobalConstants.SelectApplyPanelSelectionStructure;
        break;
      case PanelCircuitDataReturnCodes.SelectionMismatch:
        this.dialogTitle = GlobalConstants.SelectApplyPanelSelectionMismatch;
        this.dialogBody = GlobalConstants.SelectApplyPanelSelectConduit;
        break;
      default:
        break;
    }
  }

  private getConvertSystemAlertInfo(response: ConvertOptionReturnCodes): void {
    switch (response) {
      case ConvertOptionReturnCodes.NoFittingSupport:
        this.dialogBody = GlobalConstants.FittingsDoNotSupportConvertSystem;
        break;
      case ConvertOptionReturnCodes.NoSelection:
        this.dialogBody = GlobalConstants.SelectAtLeastOneFabConduit;
        break;
      case ConvertOptionReturnCodes.ConduitTypeRequired:
        this.dialogTitle = GlobalConstants.FabConduitTypeRequired;
        this.dialogBody = GlobalConstants.FabConduitTypeNotAvailable;
        break;
      case ConvertOptionReturnCodes.NoSelectionCt:
        this.dialogBody = GlobalConstants.SelectAtLeastOneCableTray;
        break;
      case ConvertOptionReturnCodes.CableTrayTypeRequired:
        this.dialogTitle = GlobalConstants.CableTrayTypeRequired;
        this.dialogBody = GlobalConstants.CableTrayTypeNotAvailable;
        break;
      case ConvertOptionReturnCodes.CableTraySizeMismatch:
        this.dialogTitle = GlobalConstants.ConvertSystem;
        this.dialogBody = GlobalConstants.CableTraySizeNotFound;
        break;
      case ConvertOptionReturnCodes.IsWireMeshAndClearance:
        this.dialogTitle = GlobalConstants.ConvertSystem;
        this.dialogBody = GlobalConstants.SelectedStructureDoesNotSupport;
        break;
      default:
        this.isMsgPopupVisible = false;
        break;
    }
  }

  //#region Download logic

  private checkIfMissingFamilies(downloadResponse: DownloadFamiliesReturnCodes, isFromToolbelt: boolean): void {
    switch (downloadResponse) {
      case DownloadFamiliesReturnCodes.Download:
        this.areMissing = true;
        this.isError = false;
        this.confirmationButton = GlobalConstants.Yes;
        this.isDisapprovalButtonDisabled = false;
        this.disapprovalButton = GlobalConstants.No;
        this.dialogTitle = GlobalConstants.DownloadMissingFamiliesTitle;
        this.dialogBody = GlobalConstants.DownloadMissingFamiliesBody;
        this.downloadType = GlobalConstants.MissingFamiliesType;
        break;
      case DownloadFamiliesReturnCodes.NotDownload:
        this.areMissing = false;
        if (isFromToolbelt) {
          this.isError = true;
          this.dialogTitle = GlobalConstants.DownloadMissingFamiliesTitle;
          this.dialogBody = GlobalConstants.NoMissingFamiliesBody;
          this.confirmationButton = GlobalConstants.Dismiss;
          this.isDisapprovalButtonDisabled = true;
        } else {
          this.isError = false;
        }
        this.checkInvalidShortcuts();
        break;
    }
    this.cd.detectChanges();
  }

  /**
   * Sets the dialog properties for the update generic fittings
   */
  private genericFittingsDialog(): void {
    this.acrylicSurfaceService.hide();
    this.areMissing = true;
    this.confirmationButton = GlobalConstants.Yes;
    this.isDisapprovalButtonDisabled = false;
    this.disapprovalButton = GlobalConstants.No;
    this.downloadType = GlobalConstants.GenericFamilies;
    this.dialogTitle = GlobalConstants.TitleForUpdateGenericFittings;
    this.dialogBody = GlobalConstants.BodyForUpdateGenericFittings;
  }

  /**
   * Logic for update generic families
   */
  private updateGenericFittingsLogic(): void {
    this.isLoading = true;
    this.progressBarTitle = GlobalConstants.Downloading;
    this.progressBarBody = GlobalConstants.GenericFamilies;
    this.cd.detectChanges();

    setTimeout(() => {
      this.facade
        .updateGenericFittings()
        .pipe(takeUntil(this.closed$))
        .subscribe((response) => {
          switch (response) {
            case DownloadFamiliesReturnCodes.Download:
              this.areMissing = false;
              this.isLoading = false;
              break;
            case DownloadFamiliesReturnCodes.Error:
              this.areMissing = false;
              this.isError = true;
              this.dialogTitle = GlobalConstants.TitleForUpdateGenericFittings;
              this.dialogBody = GlobalConstants.BodyForUpdateGenericFittingsError;
              this.confirmationButton = GlobalConstants.Dismiss;
              this.isDisapprovalButtonDisabled = true;
              break;
          }
          this.progressBarTitle = '';
          this.progressBarBody = '';
          this.cd.detectChanges();
        });
    }, 1000);
  }

  /**
   * Logic for download missing families
   */
  private missingFamiliesLogic(): void {
    this.isLoading = true;
    this.progressBarTitle = GlobalConstants.Downloading;
    this.progressBarBody = GlobalConstants.MissingFamilies;
    this.cd.detectChanges();

    setTimeout(() => {
      this.facade
        .downloadMissingFamilies()
        .pipe(takeUntil(this.closed$))
        .subscribe((response) => {
          this.isError = response;
          if (!this.isError) {
            this.dialogTitle = GlobalConstants.TitleForUnableToDownloadMissingFamilies;
            this.dialogBody = GlobalConstants.BodyForUnableToDownloadMissingFamilies;
            this.confirmationButton = GlobalConstants.Dismiss;
            this.isDisapprovalButtonDisabled = true;
          } else {
            this.checkInvalidShortcuts();
            this.isError = false;
            this.areMissing = false;
          }
          this.isLoading = false;
          this.progressBarTitle = '';
          this.progressBarBody = '';
          this.cd.detectChanges();
        });
    }, 1000);
  }

  //#endregion
}
